/**
 * Face landmark detection using MediaPipe FaceLandmarker.
 *
 * Runs entirely client-side. Uses iris diameter (~11.7 mm — biologically
 * stable across adults) as the pixel→mm scale anchor, then derives the
 * measurements that eyewear + headwear size guides ask for.
 *
 * Shares the same `@mediapipe/tasks-vision` bundle loaded by
 * `pose-detect.ts`, so the only extra cost is the ~3 MB `.task` file.
 */

import { detectBodyLandmarks, type BodyLandmarks } from "./pose-detect";

export interface Pt {
  x: number;
  y: number;
  z?: number;
}

export interface FaceLandmarks {
  noseTip: Pt;
  noseBridge: Pt;
  leftInnerEye: Pt;
  rightInnerEye: Pt;
  leftOuterEye: Pt;
  rightOuterEye: Pt;
  leftIrisCenter: Pt;
  rightIrisCenter: Pt;
  leftIrisRing: Pt[];
  rightIrisRing: Pt[];
  leftTragus: Pt;
  rightTragus: Pt;
  forehead: Pt;
  chin: Pt;
  leftMouth: Pt;
  rightMouth: Pt;
}

export interface FaceMeasurementsMm {
  irisDiameter: number;
  pd: number;
  bridgeWidth: number;
  faceWidth: number;
  templeLengthLeft: number;
  templeLengthRight: number;
  templeLength: number;
  headWidth: number;
  headDepth: number;
  headCircumference: number;
}

export interface FaceDetectResult {
  landmarks: FaceLandmarks;
  measurementsMm: FaceMeasurementsMm;
  irisConfidence: number;
  imageWidth: number;
  imageHeight: number;
}

// ── Canonical MediaPipe FaceLandmarker indices ───────────────────
// (Stable across MediaPipe Tasks versions.)
const IDX = {
  noseTip: 1,
  noseBridge: 168,
  leftInnerEye: 133,
  rightInnerEye: 362,
  leftOuterEye: 33,
  rightOuterEye: 263,
  // Iris landmarks (478-point model with iris refinement)
  leftIrisCenter: 468,
  leftIrisRing: [469, 470, 471, 472] as const,
  rightIrisCenter: 473,
  rightIrisRing: [474, 475, 476, 477] as const,
  // Tragus (ear attach point) — best approximations in the face mesh
  leftTragus: 234,
  rightTragus: 454,
  forehead: 10,
  chin: 152,
  leftMouth: 61,
  rightMouth: 291,
} as const;

// Average adult iris diameter in mm. Very stable — varies by <1 mm across adults.
const IRIS_DIAMETER_MM = 11.7;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let faceLandmarker: any = null;
let loadingPromise: Promise<void> | null = null;

async function loadFaceLandmarker(): Promise<void> {
  if (faceLandmarker) {
    console.log("[PS-SDK:FaceDetect] loadFaceLandmarker — already loaded, reusing");
    return;
  }
  if (loadingPromise) {
    console.log("[PS-SDK:FaceDetect] loadFaceLandmarker — awaiting in-flight load");
    return loadingPromise;
  }
  console.log("[PS-SDK:FaceDetect] loadFaceLandmarker — starting fresh init");
  loadingPromise = (async () => {
    try {
      console.log("[PS-SDK:FaceDetect] importing vision_bundle.mjs from CDN…");
      const t0 = performance.now();
      const vision = await import(
        /* webpackIgnore: true */
        // @ts-ignore dynamic CDN import
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/vision_bundle.mjs"
      );
      console.log(`[PS-SDK:FaceDetect] vision bundle imported in ${Math.round(performance.now() - t0)}ms`);
      const { FilesetResolver, FaceLandmarker } = vision;
      if (!FilesetResolver || !FaceLandmarker) {
        console.error("[PS-SDK:FaceDetect] vision bundle missing expected exports", { keys: Object.keys(vision) });
        throw new Error("MediaPipe vision bundle returned no FilesetResolver/FaceLandmarker");
      }
      console.log("[PS-SDK:FaceDetect] resolving WASM filesets…");
      const t1 = performance.now();
      const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/wasm"
      );
      console.log(`[PS-SDK:FaceDetect] WASM resolver ready in ${Math.round(performance.now() - t1)}ms`);
      const createFaceLandmarker = async (delegate: "GPU" | "CPU") => {
        console.log(`[PS-SDK:FaceDetect] creating FaceLandmarker (${delegate} delegate, model=latest)…`);
        const t2 = performance.now();
        const instance = await FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
            delegate,
          },
          runningMode: "IMAGE",
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        });
        console.log(`[PS-SDK:FaceDetect] FaceLandmarker ready with ${delegate} delegate in ${Math.round(performance.now() - t2)}ms`);
        return instance;
      };
      try {
        faceLandmarker = await createFaceLandmarker("GPU");
      } catch (gpuErr) {
        console.warn("[PS-SDK:FaceDetect] GPU delegate failed, retrying with CPU delegate:", gpuErr);
        faceLandmarker = await createFaceLandmarker("CPU");
      }
    } catch (err) {
      console.error("[PS-SDK:FaceDetect] loadFaceLandmarker FAILED:", err);
      loadingPromise = null; // allow future retries
      throw err;
    }
  })();
  return loadingPromise;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      console.log("[PS-SDK:FaceDetect] loadImage — primary (crossOrigin) succeeded", { w: img.naturalWidth, h: img.naturalHeight });
      resolve(img);
    };
    img.onerror = () => {
      console.warn("[PS-SDK:FaceDetect] loadImage — primary (crossOrigin) failed, retrying without crossOrigin");
      const img2 = new Image();
      img2.onload = () => {
        console.log("[PS-SDK:FaceDetect] loadImage — fallback succeeded", { w: img2.naturalWidth, h: img2.naturalHeight });
        resolve(img2);
      };
      img2.onerror = () => {
        console.error("[PS-SDK:FaceDetect] loadImage — both attempts failed for src:", src.slice(0, 120));
        reject(new Error("Failed to load image"));
      };
      img2.src = src;
    };
    img.src = src;
  });
}

function dist2D(a: Pt, b: Pt): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function irisDiameterPx(ring: Pt[], imageWidth: number, imageHeight: number): number {
  if (ring.length < 4) return 0;
  // Ring points come back in normalized coords; convert to pixels and take
  // the average of the two perpendicular diameters.
  const toPx = (p: Pt) => ({ x: p.x * imageWidth, y: p.y * imageHeight });
  const [p0, p1, p2, p3] = ring.map(toPx) as [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }, { x: number; y: number }];
  const d1 = Math.hypot(p0.x - p2.x, p0.y - p2.y);
  const d2 = Math.hypot(p1.x - p3.x, p1.y - p3.y);
  return (d1 + d2) / 2;
}

/** Extract the subset of landmarks we need from the raw FaceLandmarker output. */
function extractLandmarks(raw: Array<{ x: number; y: number; z?: number }>): FaceLandmarks | null {
  if (raw.length < 478) {
    console.warn(`[PS-SDK:FaceDetect] extractLandmarks REJECTED — need 478 landmarks (iris refinement), got ${raw.length}. The model is missing iris data — probably the CDN "latest" tag moved to a non-iris variant.`);
    return null;
  }
  const at = (i: number): Pt => ({ x: raw[i]!.x, y: raw[i]!.y, z: raw[i]!.z });
  return {
    noseTip: at(IDX.noseTip),
    noseBridge: at(IDX.noseBridge),
    leftInnerEye: at(IDX.leftInnerEye),
    rightInnerEye: at(IDX.rightInnerEye),
    leftOuterEye: at(IDX.leftOuterEye),
    rightOuterEye: at(IDX.rightOuterEye),
    leftIrisCenter: at(IDX.leftIrisCenter),
    rightIrisCenter: at(IDX.rightIrisCenter),
    leftIrisRing: IDX.leftIrisRing.map(at),
    rightIrisRing: IDX.rightIrisRing.map(at),
    leftTragus: at(IDX.leftTragus),
    rightTragus: at(IDX.rightTragus),
    forehead: at(IDX.forehead),
    chin: at(IDX.chin),
    leftMouth: at(IDX.leftMouth),
    rightMouth: at(IDX.rightMouth),
  };
}

/** Derive all mm measurements from landmarks using iris-diameter calibration. */
function computeMeasurements(
  lm: FaceLandmarks,
  imageWidth: number,
  imageHeight: number,
): { measurements: FaceMeasurementsMm; irisConfidence: number } {
  // 1. Compute px→mm scale from the iris ring(s).
  const leftPx = irisDiameterPx(lm.leftIrisRing, imageWidth, imageHeight);
  const rightPx = irisDiameterPx(lm.rightIrisRing, imageWidth, imageHeight);
  const irisPx = (leftPx + rightPx) / 2;

  // Confidence: high when both irises agree; drops when they diverge or are tiny.
  let irisConfidence = 1;
  if (irisPx < 8) irisConfidence = 0.3; // face too small / far
  else if (Math.abs(leftPx - rightPx) / irisPx > 0.3) irisConfidence = 0.5; // asymmetric → head rotated
  else if (Math.abs(leftPx - rightPx) / irisPx > 0.15) irisConfidence = 0.8;

  const pxToMm = irisPx > 0 ? IRIS_DIAMETER_MM / irisPx : 0;

  // 2. Helper that converts two landmark points → mm distance in the image plane.
  const mmBetween = (a: Pt, b: Pt): number => {
    const pxDist = Math.hypot(
      (a.x - b.x) * imageWidth,
      (a.y - b.y) * imageHeight,
    );
    return pxDist * pxToMm;
  };

  const pd = mmBetween(lm.leftIrisCenter, lm.rightIrisCenter);
  // Inner-eye-corner gap is ~1.6× the glasses DBL (bridge width at frame-rest
  // height). Divide here so the value we report matches the convention used
  // by eyewear size charts — the backend can then trust whatever it receives.
  const innerEyeGap = mmBetween(lm.leftInnerEye, lm.rightInnerEye);
  const bridgeWidth = innerEyeGap / 1.6;
  const faceWidth = mmBetween(lm.leftTragus, lm.rightTragus);
  const templeLengthLeft = mmBetween(lm.leftTragus, lm.leftOuterEye);
  const templeLengthRight = mmBetween(lm.rightTragus, lm.rightOuterEye);
  const templeLength = (templeLengthLeft + templeLengthRight) / 2;

  // Head width ≈ face width × 1.07 (tragus-to-tragus is narrower than max head width).
  const headWidth = faceWidth * 1.07;

  // Head depth: use z delta between forehead (10) and chin (152). MediaPipe's z
  // is in the same unit as x after normalization × image width, approximately.
  // This is noisy — we clamp to a plausible adult range.
  const zDepthNorm = Math.abs((lm.forehead.z ?? 0) - (lm.chin.z ?? 0));
  const rawHeadDepthMm = zDepthNorm * imageWidth * pxToMm;
  const headDepth = Math.max(170, Math.min(210, rawHeadDepthMm || 190));

  // Head circumference via the ellipse approximation: C ≈ π·√(2(a² + b²))
  const a = headWidth / 2;
  const b = headDepth / 2;
  const headCircumference = Math.PI * Math.sqrt(2 * (a * a + b * b));

  return {
    measurements: {
      irisDiameter: IRIS_DIAMETER_MM,
      pd: round1(pd),
      bridgeWidth: round1(bridgeWidth),
      faceWidth: round1(faceWidth),
      templeLengthLeft: round1(templeLengthLeft),
      templeLengthRight: round1(templeLengthRight),
      templeLength: round1(templeLength),
      headWidth: round1(headWidth),
      headDepth: round1(headDepth),
      headCircumference: round1(headCircumference),
    },
    irisConfidence,
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Full-body photos can make the face/head too small for FaceLandmarker.
 * Use BlazePose's nose + shoulders to crop a local head/upper-chest image for
 * sizing only. The original full-body photo remains the try-on input.
 */
export async function cropHeadForSizing(
  imageSrc: string | HTMLImageElement,
  providedBodyLandmarks?: BodyLandmarks | null,
): Promise<string | null> {
  try {
    const [img, body] = await Promise.all([
      typeof imageSrc === "string" ? loadImage(imageSrc) : Promise.resolve(imageSrc),
      providedBodyLandmarks ? Promise.resolve(providedBodyLandmarks) : detectBodyLandmarks(imageSrc).catch(() => null),
    ]);
    const imageWidth = img.naturalWidth || img.width;
    const imageHeight = img.naturalHeight || img.height;
    if (!imageWidth || !imageHeight) return null;

    let centerX = 0.5;
    let top = 0;
    let bottom = 0.42;
    let cropWidthNorm = 0.55;

    if (body?.nose && body.leftShoulder && body.rightShoulder) {
      const nose = body.nose;
      const lShoulder = body.leftShoulder;
      const rShoulder = body.rightShoulder;
      const shoulderY = (lShoulder.y + rShoulder.y) / 2;
      const shoulderWidth = Math.abs(rShoulder.x - lShoulder.x);
      const headToShoulder = Math.max(0.08, shoulderY - nose.y);

      centerX = clamp(nose.x, 0.15, 0.85);
      top = nose.y - headToShoulder * 0.95;
      bottom = shoulderY + headToShoulder * 0.35;
      cropWidthNorm = Math.max(shoulderWidth * 1.45, (bottom - top) * (imageHeight / imageWidth) * 0.95, 0.28);
    }

    const cropHeightNorm = clamp(bottom - top, 0.24, 0.58);
    const cropW = clamp(cropWidthNorm * imageWidth, imageWidth * 0.24, imageWidth);
    const cropH = clamp(cropHeightNorm * imageHeight, imageHeight * 0.22, imageHeight);
    const cropX = clamp(centerX * imageWidth - cropW / 2, 0, imageWidth - cropW);
    const cropY = clamp(top * imageHeight, 0, imageHeight - cropH);
    const targetLongEdge = 1536;
    const scale = Math.min(1, targetLongEdge / Math.max(cropW, cropH));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cropW * scale));
    canvas.height = Math.max(1, Math.round(cropH * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    console.log("[PS-SDK:FaceDetect] auto-cropped head for sizing", {
      source: body ? "pose" : "fallback",
      cropX: Math.round(cropX),
      cropY: Math.round(cropY),
      cropW: Math.round(cropW),
      cropH: Math.round(cropH),
      outW: canvas.width,
      outH: canvas.height,
    });
    return dataUrl;
  } catch (err) {
    console.warn("[PS-SDK:FaceDetect] cropHeadForSizing failed:", err);
    return null;
  }
}

/** Detect face landmarks and compute eyewear/headwear measurements in mm. */
export async function detectFaceMeasurements(
  imageSrc: string | HTMLImageElement,
): Promise<FaceDetectResult | null> {
  console.log("[PS-SDK:FaceDetect] detectFaceMeasurements called", {
    srcType: typeof imageSrc === "string" ? (imageSrc.startsWith("blob:") ? "blob-url" : imageSrc.startsWith("data:") ? "data-url" : "other") : "HTMLImageElement",
    srcLength: typeof imageSrc === "string" ? imageSrc.length : "N/A",
  });
  const tStart = performance.now();
  try {
    console.log("[PS-SDK:FaceDetect] step 1/4 — loading FaceLandmarker");
    await loadFaceLandmarker();
    console.log("[PS-SDK:FaceDetect] step 2/4 — loading image");
    const img = typeof imageSrc === "string" ? await loadImage(imageSrc) : imageSrc;
    const imageWidth = img.naturalWidth || img.width;
    const imageHeight = img.naturalHeight || img.height;
    console.log("[PS-SDK:FaceDetect] step 3/4 — running faceLandmarker.detect()", { imageWidth, imageHeight });
    const tDetect = performance.now();
    const result = faceLandmarker.detect(img);
    const detectMs = Math.round(performance.now() - tDetect);
    const faceCount = result?.faceLandmarks?.length ?? 0;
    console.log(`[PS-SDK:FaceDetect] detect() returned in ${detectMs}ms — faces: ${faceCount}`);
    if (!faceCount) {
      console.warn("[PS-SDK:FaceDetect] NO FACES detected — returning null");
      return null;
    }
    const raw = result.faceLandmarks[0];
    console.log(`[PS-SDK:FaceDetect] first face has ${raw.length} landmarks`);
    const landmarks = extractLandmarks(raw);
    if (!landmarks) {
      return null;
    }
    console.log("[PS-SDK:FaceDetect] step 4/4 — computing measurements");
    const { measurements, irisConfidence } = computeMeasurements(landmarks, imageWidth, imageHeight);
    console.log(`[PS-SDK:FaceDetect] ✓ DONE in ${Math.round(performance.now() - tStart)}ms`, { irisConfidence, measurements });
    return { landmarks, measurementsMm: measurements, irisConfidence, imageWidth, imageHeight };
  } catch (err) {
    console.error("[PS-SDK:FaceDetect] detectFaceMeasurements FAILED:", err);
    return null;
  }
}
